import _Download from "@mui/icons-material/Download";
import _InputBase from "@mui/material/InputBase";
import _Search from "@mui/icons-material/Search";
import _ExpandMore from "@mui/icons-material/ExpandMore";
import _InfoOutlined from "@mui/icons-material/InfoOutlined";
import _Typography from "@mui/material/Typography";
import _Tooltip from "@mui/material/Tooltip";
import _Box from "@mui/material/Box";
import _GlobalStyles from "@mui/material/GlobalStyles";
import _VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import _VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import { useTheme } from '@mui/material/styles';
import { cellToLatLng } from 'h3-js';
import { Marker, Popup } from 'maplibre-gl';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import bfgIconBushSrc from '../../assets/images/bfg/game-icon-bush.png';
import bfgIconForestSrc from '../../assets/images/bfg/game-icon-forest.png';
import bfgIconSoilSrc from '../../assets/images/bfg/game-icon-soil.png';
import bfgIconSproutSrc from '../../assets/images/bfg/game-icon-sprout.png';
import bfgIconTreeSrc from '../../assets/images/bfg/game-icon-tree.png';
import bfgSiteMarkerIconSrc from '../../assets/images/bfg/site-marker-office-building.png';
import bfgSiteTooltipIconSrc from '../../assets/images/bfg/site-tooltip-icon.svg';
import { useUnits } from '../../hooks';
import { gamePMTilesSourceId, useGamePMTiles, useGameTiles, useGeoveloMap, useSource } from '../../hooks/map';
import { useAmplitudeTracker } from '../../hooks/tracker';
import useGameQuery from '../../hooks/use-game-query';
import useGameTeamsQuery from '../../hooks/use-game-teams-query';
import { Place } from '../../models';
import { AppService } from '../../services';
import { getDataFromUrl } from '../../utils';
import { ButtonControl } from '../map';
import { bfgBorderColorExpression, bfgBorderOpacityExpression, bfgBorderWidthExpression, bfgFillColorExpression, bfgFillOpacityExpression, bfgHeatmapFillOpacityExpression, bfgIconBushId, bfgIconForestId, bfgIconImageExpression, bfgIconSizeExpression, bfgIconSproutId, bfgIconTreeId, bfgPassageBuckets, bfgPmtilesLayerId, buildBfgHeatmapFillColorExpression, buildBfgHeatmapLegendItems, computeBfgFeatureState, getBfgHeatmapFillColor, getBfgIconTier, getBfgTeamQuantile, getBfgTeamValue, getBfgTierLabel } from './exploration-map-game-style';
const containerId = 'event-exploration-map-game';
const bfgIconPointsSourceId = `${bfgPmtilesLayerId}-icon-points`;
const bfgIconLayerId = `${bfgPmtilesLayerId}-icons`;
// Hexagons (fill, borders, growth icons) only show once zoomed in enough to be legible — hidden
// at wider/regional zoom levels.
export const bfgMinZoom = 8;
export const bfgSiteZoom = 12;
// Same fallback center as geovelo's own game webview.
export const toursCenter = {
  lat: 47.394144,
  lng: 0.68484
};
// The game map's own dedicated style, served on the same tile server as the rest of the app's
// styles (same partner, resolves to prod/dev/staging per environment) but under its own dir —
// fetched directly (see the map-init effect below), independent of the shared base/toner styles.
export const gameStyleDir = 'game-style-v1.0-vector';
// Reuse the exact same illustrations as the map icons in the click popup, for visual continuity.
const bfgIconSrcByTier = {
  soil: bfgIconSoilSrc,
  sprout: bfgIconSproutSrc,
  bush: bfgIconBushSrc,
  tree: bfgIconTreeSrc,
  forest: bfgIconForestSrc
};
export async function loadIcon(map, id, url) {
  if (map.hasImage(id)) return;
  const image = await map.loadImage(url);
  if (!map.hasImage(id)) map.addImage(id, image.data);
}
const bfgSiteMarkerSize = 40;
const bfgSiteMarkerWidth = bfgSiteMarkerSize * 28 / 34;

// figma.com/design/K4kH3Lz6mlMbw1icekSHP2?node-id=6220-292549 — white card, building illustration,
// site name + passage count. `points` is null until the marker's hex has been resolved and its
// team value looked up (see the site-marker effect in ExplorationMapGame), so it's left off until
// then rather than showing a misleading 0.
function buildBfgSiteTooltipHtml(title, isMine, points, t, isDarkTheme) {
  const label = isMine ? `${title} · ${t('commons.challenges.exploration_map.map.my_site')}` : title;
  const textColor = isDarkTheme ? '#F5F5F5' : '#2C2E30';
  const mutedColor = isDarkTheme ? '#B5B8BC' : '#666A6F';
  const passagesHtml = points === null ? '' : `<div style="font-size:12px;color:${mutedColor};">${t('commons.challenges.exploration_map.map.passages', {
    count: points
  })}</div>`;
  return `<div style="display:flex;align-items:center;gap:8px;">
    <img alt="" src="${bfgSiteTooltipIconSrc}" style="width:23px;height:27px;object-fit:contain;flex-shrink:0;" />
    <div>
      <div style="font-weight:600;font-size:12px;color:${textColor};">${label}</div>
      ${passagesHtml}
    </div>
  </div>`;
}

// Same pin design as the companies-only isochrones site marker (packages/companies/src/pages/
// cyclability/isochrones/site-marker.ts) — copied here since commons can't import from the
// companies app — so a site looks the same wherever it's shown. Every site gets the same look
// (no dimming/greying for sites that aren't the player's own); only the tooltip text says which
// one is "mine".
export function createBfgSiteMarker(label, isMine, t, isDarkTheme) {
  const el = document.createElement('div');
  el.style.width = `${bfgSiteMarkerWidth}px`;
  el.style.height = `${bfgSiteMarkerSize}px`;
  el.innerHTML = `<svg width="${bfgSiteMarkerWidth}" height="${bfgSiteMarkerSize}" viewBox="-1 -1 30 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fill-rule="evenodd" clip-rule="evenodd"
      d="M16.8023 27.7195C23.1914 26.4215 28 20.7723 28 14C28 6.26801 21.732 0 14 0C6.26801 0 0 6.26801 0 14C0 20.7723 4.80859 26.4215 11.1977 27.7195L13.0667 32.5753C13.3956 33.4297 14.6044 33.4297 14.9333 32.5753L16.8023 27.7195Z"
      fill="#F260A7" stroke="#BA3977" stroke-width="0.8"/>
    <circle cx="14" cy="13.5" r="11.5" fill="#A82E69"/>
    <image href="${bfgSiteMarkerIconSrc}" x="7" y="6.5" width="14" height="14"/>
  </svg>`;
  const marker = new Marker({
    anchor: 'bottom',
    element: el
  });
  const popup = new Popup({
    className: 'bfg-site-popup',
    closeButton: false,
    closeOnClick: false,
    offset: bfgSiteMarkerSize - 4
  }).setHTML(buildBfgSiteTooltipHtml(label, isMine, null, t, isDarkTheme));
  marker.setPopup(popup);
  el.addEventListener('mouseenter', () => {
    if (!popup.isOpen()) marker.togglePopup();
  });
  el.addEventListener('mouseleave', () => {
    if (popup.isOpen()) marker.togglePopup();
  });
  return marker;
}
/**
 * Where the map opens by default (before the player's live location, if any, is known — see the
 * geolocation-camera effect in ExplorationMapGame, which re-centers on top of this once/if it
 * resolves): the player's own site takes priority (closest, most relevant zoom), then all of the
 * company's sites (zoomed out to fit them), then the company HQ address, and only falls back to
 * the fixed Tours center if none of those have a known location — mirrors the same site/HQ
 * fallback logic already used by the isochrones map (packages/companies/src/pages/cyclability/
 * isochrones).
 */
export function getBfgInitialCamera(sites, headquarters, mySite) {
  if (mySite?.place) {
    const [lng, lat] = mySite.place.point.coordinates;
    return {
      center: {
        lat,
        lng
      },
      zoom: bfgSiteZoom
    };
  }
  const sitesWithPlace = (sites ?? []).filter(site => site.place);
  if (sitesWithPlace.length > 0) {
    const coordinates = sitesWithPlace.map(site => site.place.point.coordinates);
    const lngs = coordinates.map(([lng]) => lng);
    const lats = coordinates.map(([, lat]) => lat);
    return {
      bounds: [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)],
      fitBoundsOptions: {
        padding: 80,
        maxZoom: 12
      }
    };
  }
  if (headquarters?.place) {
    const [lng, lat] = headquarters.place.point.coordinates;
    return {
      center: {
        lat,
        lng
      },
      zoom: 11
    };
  }
  return {
    center: toursCenter,
    zoom: 10
  };
}

// Compact click popup, matching the Figma reference (node 4613:155501): an icon, a title, and the
// passage count — nothing else, for every hex including company sites/HQs (those no longer get a
// special site card — same tier-based popup as any other tile). In heatmap mode, the fixed-tier
// icon/label (meaningless against a quantile fill) is swapped for a swatch of the hex's own
// heatmap color, with just the passage count next to it — no tier/quantile title.
function buildBfgPopupHtml(tier, points, colorMode, t, isDarkTheme, viewMode, heatmapColor) {
  const passagesText = t(colorMode === 'commuting' ? 'commons.challenges.exploration_map.map.commuting_trips' : 'commons.challenges.exploration_map.map.passages', {
    count: points
  });
  const textColor = isDarkTheme ? '#F5F5F5' : '#2C2E30';
  const mutedColor = isDarkTheme ? '#B5B8BC' : '#95999E';
  if (viewMode === 'heatmap' && heatmapColor) {
    const swatchHtml = `<div style="width:20px;height:20px;flex-shrink:0;border-radius:4px;background:${heatmapColor};"></div>`;
    return `<div style="display:flex;align-items:center;gap:10px;">
      ${swatchHtml}
      <div style="font-weight:700;font-size:14px;color:${textColor};">${passagesText}</div>
    </div>`;
  }
  const title = getBfgTierLabel(tier, t);
  // "Non exploré" (soil) has no growth stage to depict, so no icon.
  const iconHtml = tier === 'soil' ? '' : `<img alt="" src="${bfgIconSrcByTier[tier]}" style="width:32px;height:32px;object-fit:contain;flex-shrink:0;transform:rotate(15deg);" />`;
  return `<div style="display:flex;align-items:center;gap:10px;">
    ${iconHtml}
    <div>
      <div style="font-weight:700;font-size:14px;color:${textColor};">${title}</div>
      <div style="font-size:12px;color:${mutedColor};margin-top:1px;">${passagesText}</div>
    </div>
  </div>`;
}
export function ExplorationMapGame({
  gameId,
  ownGeogroupId,
  headquartersGeogroup,
  mySiteGeogroup,
  sites,
  initialColorMode,
  isDarkTheme,
  isWebview
}) {
  const {
    t
  } = useTranslation();
  const {
    trackEvent
  } = useAmplitudeTracker();
  const theme = useTheme();
  const vegetationControlRef = useRef(null);
  // Dark-theme surface palette (isDarkTheme) — only the chrome around/on top of the map (loading
  // background, control pills, legend, popups); the map's own hex styling is untouched.
  const uiColors = {
    background: isDarkTheme ? '#1B1B1B' : undefined,
    surface: isDarkTheme ? '#2A2A2A' : '#fff',
    surfaceMuted: isDarkTheme ? '#242424' : '#E4F2DE',
    textPrimary: isDarkTheme ? '#F5F5F5' : '#2C2E30',
    textSecondary: isDarkTheme ? '#B5B8BC' : '#666A6F',
    textMuted: isDarkTheme ? '#8A8D91' : '#95999E',
    border: isDarkTheme ? '#3A3A3A' : '#EEF1EE'
  };
  const colorModes = [{
    key: 'score',
    label: t('commons.challenges.exploration_map.map.color_mode_score'),
    shortLabel: t('commons.challenges.exploration_map.map.color_mode_score_short')
  }, {
    key: 'commuting',
    label: t('commons.challenges.exploration_map.map.color_mode_commuting'),
    shortLabel: t('commons.challenges.exploration_map.map.color_mode_commuting_short')
  }];
  // figma.com/design/K4kH3Lz6mlMbw1icekSHP2?node-id=6799-354748 — "Paliers" (fixed growth-tier
  // fill) vs "Carte de chaleur" (per-team quantile heatmap, see TBfgViewMode) segmented toggle.
  const tierViewModes = [{
    key: 'tiers',
    label: t('commons.challenges.exploration_map.map.view_mode_tiers'),
    tooltipTitle: t('commons.challenges.exploration_map.map.view_mode_tiers_tooltip_title'),
    tooltipBody: t('commons.challenges.exploration_map.map.view_mode_tiers_tooltip_body')
  }, {
    key: 'heatmap',
    label: t('commons.challenges.exploration_map.map.view_mode_heatmap'),
    tooltipTitle: t('commons.challenges.exploration_map.map.view_mode_heatmap_tooltip_title'),
    tooltipBody: t('commons.challenges.exploration_map.map.view_mode_heatmap_tooltip_body')
  }];
  const [colorMode, setColorMode] = useState(initialColorMode || 'score');
  const [tierViewMode, setTierViewMode] = useState('tiers');
  const [showVegetation, setShowVegetation] = useState(true);
  const [siteMenuOpen, setSiteMenuOpen] = useState(false);
  const [siteSearchQuery, setSiteSearchQuery] = useState('');
  const [zoom, setZoom] = useState(null);
  // Feeds the map's built-in "you are here" dot (useGeoveloMap's locateControl option, see below)
  // — set both by the locate button itself and, silently, on mount if permission was already
  // granted (see the geolocation-watch effect further down).
  const [currentGeolocation, setCurrentGeolocation] = useState(null);
  // Consumed by the geolocation-camera effect below the first time currentGeolocation resolves, so
  // it only ever overrides the opening camera once — a *later* location update (eg. the dot
  // drifting slightly as GPS gets a better fix) shouldn't yank the view back to it if the player
  // has since panned/zoomed away on their own.
  const appliedInitialGeolocationCameraRef = useRef(false);
  // Shared between the hover effect and the click/popup effect below so the hover glow stays on
  // the tile whose popup is open even after the cursor leaves it (or moves to another tile) —
  // cleared once that popup actually closes.
  const selectedTileIdRef = useRef(null);
  // Full-bleed breakout (see the root Box below): measured against the stable parent box rather
  // than this component's own rect, since our own rect moves once the computed margin/width are
  // applied — measuring self would zero the delta out on the next resize/observer tick. width is
  // measured explicitly (not left+right margins) because an ancestor flex column in PageLayout
  // uses alignItems:"start", which shrink-wraps children to their intrinsic width instead of
  // stretching them.
  const bleedRootRef = useRef(null);
  // height is measured too (viewport bottom minus this box's own top) so the map fills exactly to
  // the bottom of the screen — a fixed calc(100vh - Npx) guess drifts out of sync (leaving a gap
  // or clipping) every time padding/header/title height above it changes.
  const [bleedMargins, setBleedMargins] = useState(null);
  const {
    data: game
  } = useGameQuery(gameId, '{id title theme mode covered_zone start_datetime end_datetime pmtiles_file}');
  const {
    formatNumber
  } = useUnits();
  const {
    map,
    initialized: baseMapInitialized,
    init: initMap
  } = useGeoveloMap({
    currentGeolocation,
    handleGeolocationChange: coords => setCurrentGeolocation(coords ? new Place(undefined, {
      type: 'Point',
      coordinates: [coords.lng, coords.lat]
    }) : null)
  });
  const [customStyleLoaded, setCustomStyleLoaded] = useState(false);
  useEffect(() => {
    if (!map || !baseMapInitialized) return;
    let cancelled = false;
    setCustomStyleLoaded(false);
    getDataFromUrl(`${AppService.environment.tileServer.url}/styles/${gameStyleDir}/style.json`).then(({
      version,
      sources,
      layers,
      glyphs,
      sprite
    }) => {
      if (cancelled) return;

      // `setStyle` fully replaces the map's style (wiping any source/layer another hook may have
      // already added against the previous one) and loads asynchronously — every other effect in
      // this component keys off `initialized` below, which only flips once this settles, so
      // nothing tries adding hex layers before the custom style is actually in place.
      map.once('styledata', () => {
        if (!cancelled) setCustomStyleLoaded(true);
      });
      map.setStyle({
        version: version || 8,
        sources,
        layers,
        glyphs,
        sprite
      });
    });
    return () => {
      cancelled = true;
    };
  }, [map, baseMapInitialized]);
  // Shadows the hook's own `initialized` — every effect below assumes it means "the map's final
  // style (hex layers, custom background, …) is actually in place", which now also requires the
  // custom style fetch above to have completed, not just the initial default-style map creation.
  const initialized = baseMapInitialized && customStyleLoaded;
  const usesPMTiles = Boolean(game?.pmTilesFileUrl);
  // Legacy (non-PMTiles) endpoint mode: adds its own tile source/layers as a side effect — the
  // returned teams/tiles aren't otherwise needed here (they only fed the tile-details debug panel).
  useGameTiles({
    game: usesPMTiles ? undefined : game,
    map,
    mapInitialized: initialized
  });
  const {
    data: myTeamsResult
  } = useGameTeamsQuery(usesPMTiles ? gameId : undefined, {
    groupIds: ownGeogroupId ? [ownGeogroupId] : undefined
  });
  const myTeam = usesPMTiles && ownGeogroupId ? myTeamsResult?.[0] ?? null : null;
  const myTeamId = myTeam?.id ?? null;
  // Team's own quantile boundaries for the currently active score/commuting mode (5 non-decreasing
  // upper bounds, e.g. [1, 1, 2, 3, 112] — ties are common on skewed distributions). Drives both
  // the heatmap fill (tie-merged, see buildBfgHeatmapFillColorExpression) and its legend.
  const heatmapQuantiles = (colorMode === 'commuting' ? myTeam?.commutingVisitCountQuantiles : myTeam?.visitCountQuantiles) ?? [];
  const computeFeatureState = useCallback(properties => computeBfgFeatureState(properties, colorMode, myTeamId), [colorMode, myTeamId]);
  const {
    sourceLayer
  } = useGamePMTiles({
    game: usesPMTiles ? game : undefined,
    map,
    mapInitialized: initialized,
    computeFeatureState
  });
  const {
    addGeoJSONSource: addIconPointsSource,
    updateGeoJSONSource: updateIconPointsSource
  } = useSource(map, bfgIconPointsSourceId);

  // Break this card out of PageLayout's padding AND its 1080px-capped, centered content column
  // (packages/companies/src/layouts/page/index.tsx) — only this component, not the rest of the
  // page. #page-layout-content is that layout's outer, uncapped, drawer-aware box; we measure the
  // gap between it and our own (unshifted) parent and apply it as negative margin + explicit width.
  // Depends on `game` because the root Box (and bleedRootRef) doesn't exist in the DOM until then
  // (see the `if (!game) return null` guard below) — without this, the very first run finds a
  // null ref, bails out, and (with an empty dep array) never gets another chance to run.
  useEffect(() => {
    const target = document.getElementById('page-layout-content');
    const parent = bleedRootRef.current?.parentElement;
    if (!target || !parent) return;
    function measure() {
      if (!target || !parent) return;
      const targetRect = target.getBoundingClientRect();
      const parentRect = parent.getBoundingClientRect();
      // This box's own top isn't affected by its own height (only by what's above it), so reading
      // it here — then using it to set that same height below — isn't a feedback loop.
      const rootRect = bleedRootRef.current?.getBoundingClientRect();
      setBleedMargins({
        height: rootRect ? window.innerHeight - rootRect.top : 500,
        marginLeft: targetRect.left - parentRect.left,
        width: targetRect.width
      });
    }
    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(target);
    window.addEventListener('resize', measure);
    // PageLayout's header-image collapse (see collapseHeaderImage) animates over 0.4s and this
    // component can mount at any point during that transition (eg. as soon as `game` arrives from
    // its own fetch, which races independently) — ResizeObserver tracks it live, but a final
    // re-measure once the animation is guaranteed to be over catches any frame that got missed,
    // instead of leaving a random leftover gap depending on exactly when we happened to mount.
    const settleTimeout = setTimeout(measure, 500);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', measure);
      clearTimeout(settleTimeout);
    };
  }, [game]);

  // Default camera: player's own site > all company sites > company HQ > fixed Tours fallback
  // (see getBfgInitialCamera). Only computed once, on the initial mount — the map isn't
  // re-centered if these props change afterwards.
  useEffect(() => {
    if (game) {
      initMap({
        container: containerId,
        customZoomControls: true,
        locateControl: true,
        // Same zoom as every other single-point-of-interest camera move on this map (see
        // bfgSiteZoom) — otherwise "locate me" used to zoom in further (the hook's own default,
        // 15) than opening on your own site does, so the two felt inconsistent depending on which
        // one happened to run.
        locateControlZoom: bfgSiteZoom,
        scaleControl: false,
        ...(usesPMTiles ? {
          minZoom: bfgMinZoom - 2,
          maxTileCacheZoomLevels: 0
        } : undefined),
        ...getBfgInitialCamera(sites, headquartersGeogroup, mySiteGeogroup)
      });
    }
  }, [game, usesPMTiles]);
  useEffect(() => {
    if (!usesPMTiles || !map || !initialized) return;
    function handleZoom() {
      setZoom(map.getZoom());
    }
    map.on('zoom', handleZoom);
    handleZoom();
    return () => {
      map.off('zoom', handleZoom);
    };
  }, [usesPMTiles, map, initialized]);

  // If the browser already has geolocation permission (granted on a previous visit, or to the app
  // generally), show the "you are here" dot straight away instead of waiting for the locate
  // button to be clicked — but never prompt for it here; that's still only via the button.
  useEffect(() => {
    if (!('permissions' in navigator) || !('geolocation' in navigator)) return;
    let cancelled = false;
    navigator.permissions.query({
      name: 'geolocation'
    }).then(status => {
      if (cancelled || status.state !== 'granted') return;
      navigator.geolocation.getCurrentPosition(({
        coords: {
          latitude: lat,
          longitude: lng
        }
      }) => {
        if (!cancelled) {
          setCurrentGeolocation(new Place(undefined, {
            type: 'Point',
            coordinates: [lng, lat]
          }));
        }
      });
    }).catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // If a location is (or becomes) known before the player has had a location-based camera move
  // yet, open there instead of on the site/HQ/Tours fallback getBfgInitialCamera already put the
  // camera on — same zoom as that fallback's own site case (bfgSiteZoom) so it doesn't also change
  // how zoomed in the opening view is. Only ever fires once (see
  // appliedInitialGeolocationCameraRef): a location arriving later (silent permission check
  // resolving after the initial fallback already rendered) still gets to override it, but the
  // player's own subsequent pans/zooms/locate-button clicks are never fought after that.
  useEffect(() => {
    if (!map || !currentGeolocation || appliedInitialGeolocationCameraRef.current) return;
    appliedInitialGeolocationCameraRef.current = true;
    const [lng, lat] = currentGeolocation.point.coordinates;
    map.jumpTo({
      center: {
        lat,
        lng
      },
      zoom: bfgSiteZoom
    });
  }, [map, currentGeolocation]);

  // Re-creates + recolors the "you are here" dot (useGeoveloMap's shared geolocation-* source and
  // layers, added once by locateControl on the map's *initial* default style) for this map only —
  // those layers default to a plain blue everywhere else in the app, and this game reskins the
  // outer halo/inner cursor without touching that default. Has to actually *recreate* the source
  // and layers here, not just recolor whatever's already there: `map.setStyle(...)` above (to swap
  // in the game's own dedicated style) fully replaces the style, wiping the 'geolocation' source
  // and its layers along with everything else not part of the new style — after that, they simply
  // don't exist any more, so useGeoveloMap's own currentGeolocation-driven updates silently become
  // no-ops (its `updateGeoJSONSource` bails out when `map.getSource('geolocation')` is null) and
  // the dot never reappears even though "locate me" still correctly flies the camera there (that
  // part doesn't depend on the layer).
  useEffect(() => {
    if (!map || !initialized) return;
    if (map.getSource('geolocation')) return;
    map.addSource('geolocation', {
      type: 'geojson',
      data: currentGeolocation ? {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: currentGeolocation.point,
          properties: {}
        }]
      } : {
        type: 'FeatureCollection',
        features: []
      }
    });
    map.addLayer({
      id: 'geolocation-border',
      type: 'circle',
      source: 'geolocation',
      paint: {
        'circle-radius': 10,
        'circle-color': '#69FFBE',
        'circle-opacity': 0.5
      }
    });
    map.addLayer({
      id: 'geolocation-background',
      type: 'circle',
      source: 'geolocation',
      paint: {
        'circle-radius': 6,
        'circle-color': 'white',
        'circle-opacity': 1
      }
    });
    map.addLayer({
      id: 'geolocation-forgeround',
      type: 'circle',
      source: 'geolocation',
      paint: {
        'circle-radius': 4,
        'circle-color': '#2AC682',
        'circle-opacity': 1
      }
    });
  }, [map, initialized, currentGeolocation]);

  // Company sites (or the HQ, if there are no sites) shown as pin markers — same design as the
  // companies-only isochrones site marker. Markers start at the site's real coordinate, then (on
  // PMTiles maps) get snapped to the center of whichever hex they fall in, once that hex has
  // actually rendered — a site sitting a few meters off its hex's true center otherwise reads as
  // slightly misaligned against the hex grid. Re-checked on every new tile batch (sourcedata) and
  // pan/zoom (moveend), same as the grid itself, since a site's hex can only be queried once it's
  // on screen and loaded.
  useEffect(() => {
    if (!map || !initialized) return;
    const markers = [];
    const sitesWithPlace = (sites ?? []).filter(site => site.place);
    const targets = [];
    if (sitesWithPlace.length > 0) {
      for (const site of sitesWithPlace) {
        const [lng, lat] = site.place.point.coordinates;
        const title = t('commons.challenges.exploration_map.map.site_marker_title', {
          title: site.title
        });
        const isMine = site.id === mySiteGeogroup?.id;
        const marker = createBfgSiteMarker(title, isMine, t, isDarkTheme).setLngLat({
          lat,
          lng
        }).addTo(map);
        markers.push(marker);
        targets.push({
          isMine,
          lat,
          lng,
          marker,
          title
        });
      }
    } else if (headquartersGeogroup?.place) {
      const [lng, lat] = headquartersGeogroup.place.point.coordinates;
      const title = t('commons.challenges.exploration_map.map.hq_marker_title', {
        title: headquartersGeogroup.title
      });
      const marker = createBfgSiteMarker(title, false, t, isDarkTheme).setLngLat({
        lat,
        lng
      }).addTo(map);
      markers.push(marker);
      targets.push({
        isMine: false,
        lat,
        lng,
        marker,
        title
      });
    }
    if (!usesPMTiles || targets.length === 0) {
      return () => {
        markers.forEach(marker => marker.remove());
      };
    }
    function snapToHexCenters() {
      if (!map.getLayer(bfgPmtilesLayerId)) return;
      for (const {
        isMine,
        lat,
        lng,
        marker,
        title
      } of targets) {
        const features = map.queryRenderedFeatures(map.project({
          lat,
          lng
        }), {
          layers: [bfgPmtilesLayerId]
        });
        const feature = features[0];
        const h3Index = feature?.properties?.h3_index;
        if (typeof h3Index !== 'string') continue;
        const [centerLat, centerLng] = cellToLatLng(h3Index);
        marker.setLngLat({
          lat: centerLat,
          lng: centerLng
        });
        const points = getBfgTeamValue(feature.properties, colorMode, myTeamId);
        marker.getPopup()?.setHTML(buildBfgSiteTooltipHtml(title, isMine, points, t, isDarkTheme));
      }
    }
    function handleSourceData(event) {
      if (event.sourceId === gamePMTilesSourceId && event.isSourceLoaded) snapToHexCenters();
    }
    map.on('sourcedata', handleSourceData);
    map.on('moveend', snapToHexCenters);
    snapToHexCenters();
    return () => {
      map.off('sourcedata', handleSourceData);
      map.off('moveend', snapToHexCenters);
      markers.forEach(marker => marker.remove());
    };
  }, [map, initialized, sites, headquartersGeogroup, mySiteGeogroup, usesPMTiles, colorMode, myTeamId, t, isDarkTheme]);

  // bfg_2026 tile styling: colored by total passages (see exploration-map-game-style.ts).
  useEffect(() => {
    if (!usesPMTiles || !map || !initialized || !sourceLayer) return;
    if (map.getLayer(bfgPmtilesLayerId)) return;

    // 'place_other' is the game's own dedicated style's label layer — falls back to appending on
    // top (no anchor) if it's ever missing.
    const beforeId = map.getLayer('place_other') ? 'place_other' : undefined;
    map.addLayer({
      id: bfgPmtilesLayerId,
      type: 'fill',
      source: gamePMTilesSourceId,
      'source-layer': sourceLayer,
      minzoom: bfgMinZoom,
      paint: {
        'fill-color': bfgFillColorExpression,
        'fill-opacity': bfgFillOpacityExpression,
        'fill-opacity-transition': {
          duration: 150,
          delay: 0
        }
      }
    }, beforeId);
    map.addLayer({
      id: `${bfgPmtilesLayerId}-borders`,
      type: 'line',
      source: gamePMTilesSourceId,
      'source-layer': sourceLayer,
      minzoom: bfgMinZoom,
      paint: {
        'line-color': bfgBorderColorExpression,
        'line-opacity': bfgBorderOpacityExpression,
        'line-width': bfgBorderWidthExpression
      }
    }, beforeId);
    async function addIcons() {
      await Promise.all([loadIcon(map, bfgIconSproutId, bfgIconSproutSrc), loadIcon(map, bfgIconBushId, bfgIconBushSrc), loadIcon(map, bfgIconTreeId, bfgIconTreeSrc), loadIcon(map, bfgIconForestId, bfgIconForestSrc)]);

      // Icons are drawn from a GeoJSON point source we build ourselves (see the effect below),
      // one point per hex at its exact center — same approach as geovelo's Pedal Bloom. A single
      // data-driven layer works here (icon-image/icon-size read a plain `tier` property via
      // `get`, not feature-state, which MapLibre bans in layout properties), and
      // icon-allow-overlap can stay true without any tile-boundary duplicates, since we
      // deduplicate by h3 index ourselves before adding a point.
      addIconPointsSource();
      if (!map.getLayer(bfgIconLayerId)) {
        map.addLayer({
          id: bfgIconLayerId,
          type: 'symbol',
          source: bfgIconPointsSourceId,
          minzoom: bfgMinZoom,
          layout: {
            'icon-image': bfgIconImageExpression,
            'icon-size': bfgIconSizeExpression,
            'icon-anchor': 'center',
            'icon-rotate': 15,
            'symbol-placement': 'point',
            'icon-allow-overlap': true
          }
        });
      }
    }
    addIcons().catch(err => console.error('[ExplorationMapGame] icons', err));
  }, [usesPMTiles, map, initialized, sourceLayer]);

  // "Paliers" vs "Carte de chaleur" toggle: swaps the hex fill's paint expressions rather than
  // rebuilding the layer — both bfgFillColorExpression/the heatmap expression read off
  // feature-state that's always kept up to date for both (see computeBfgFeatureState), so this is
  // just picking which one the layer is currently bound to. The heatmap fill-color is rebuilt from
  // the team's own quantile boundaries (heatmapQuantiles) so tied buckets merge (see
  // buildBfgHeatmapFillColorExpression) — keyed off its joined values rather than the array itself,
  // since a fresh `?? []` array is a new reference every render.
  const heatmapQuantilesKey = heatmapQuantiles.join(',');
  useEffect(() => {
    if (!usesPMTiles || !map || !initialized || !map.getLayer(bfgPmtilesLayerId)) return;
    map.setPaintProperty(bfgPmtilesLayerId, 'fill-color', tierViewMode === 'heatmap' ? buildBfgHeatmapFillColorExpression(heatmapQuantiles) : bfgFillColorExpression);
    map.setPaintProperty(bfgPmtilesLayerId, 'fill-opacity', tierViewMode === 'heatmap' ? bfgHeatmapFillOpacityExpression : bfgFillOpacityExpression);
  }, [usesPMTiles, map, initialized, tierViewMode, heatmapQuantilesKey]);

  // Builds the icon points: one per hex (deduplicated by h3 index, placed at its exact center via
  // h3-js), skipping bare/unexplored ("soil") hexes entirely — those stay bare, with no icon and
  // no fill (see bfgFillOpacityExpression), rather than the fog-of-war fade this used to do.
  useEffect(() => {
    if (!usesPMTiles || !map || !initialized || !sourceLayer) return;
    let refreshTimeout;
    function scheduleRefreshIconPoints() {
      clearTimeout(refreshTimeout);
      refreshTimeout = setTimeout(refreshIconPoints, 150);
    }
    function refreshIconPoints() {
      const features = map.querySourceFeatures(gamePMTilesSourceId, {
        sourceLayer: sourceLayer
      });
      const pointsByH3Index = new globalThis.Map();
      for (const feature of features) {
        const h3Index = feature.properties?.h3_index;
        if (typeof h3Index !== 'string' || pointsByH3Index.has(h3Index)) continue;
        const tier = getBfgIconTier(feature.properties, colorMode, myTeamId);
        if (tier === 'soil') continue;
        const [lat, lng] = cellToLatLng(h3Index);
        pointsByH3Index.set(h3Index, {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [lng, lat]
          },
          properties: {
            tier
          }
        });
      }
      updateIconPointsSource({
        type: 'FeatureCollection',
        features: Array.from(pointsByH3Index.values())
      });
    }
    function handleSourceData(event) {
      if (event.sourceId === gamePMTilesSourceId && event.isSourceLoaded) scheduleRefreshIconPoints();
    }
    map.on('sourcedata', handleSourceData);
    map.on('moveend', scheduleRefreshIconPoints);
    refreshIconPoints();
    return () => {
      clearTimeout(refreshTimeout);
      map.off('sourcedata', handleSourceData);
      map.off('moveend', scheduleRefreshIconPoints);
    };
  }, [usesPMTiles, map, initialized, sourceLayer, colorMode, myTeamId]);

  // "Masquer la végétation" toggle — hides/shows just the growth icons, leaving the colored hex
  // grid itself untouched. Also forced off in heatmap mode: the growth-tier icons only make sense
  // alongside the fixed tiers fill, not the quantile heatmap.
  useEffect(() => {
    if (!usesPMTiles || !map || !initialized || !map.getLayer(bfgIconLayerId)) return;
    map.setLayoutProperty(bfgIconLayerId, 'visibility', showVegetation && tierViewMode === 'tiers' ? 'visible' : 'none');
  }, [usesPMTiles, map, initialized, showVegetation, tierViewMode]);

  // Vegetation toggle button — a plain MapLibre ButtonControl (same as the zoom/locate buttons
  // added by useGeoveloMap) so it's identical in size/style and, since MapLibre prepends new
  // bottom-right controls above existing ones, lands right above the zoom pill automatically.
  // Only mounted in 'tiers' mode — see the icon-visibility effect above for why it's meaningless
  // in heatmap mode.
  useEffect(() => {
    if (!usesPMTiles || !map || !initialized || tierViewMode !== 'tiers') return;
    vegetationControlRef.current = new ButtonControl('bfg-vegetation-control', {
      icon: showVegetation ? /*#__PURE__*/React.createElement(_VisibilityOutlined, {
        sx: {
          color: '#326AC2'
        }
      }) : /*#__PURE__*/React.createElement(_VisibilityOffOutlined, null),
      title: t(showVegetation ? 'commons.challenges.exploration_map.map.hide_vegetation' : 'commons.challenges.exploration_map.map.show_vegetation')
    }, () => setShowVegetation(show => !show));
    map.addControl(vegetationControlRef.current, 'bottom-right');
    vegetationControlRef.current.init(theme);
    return () => {
      if (vegetationControlRef.current && map.hasControl(vegetationControlRef.current)) {
        map.removeControl(vegetationControlRef.current);
      }
      vegetationControlRef.current = null;
    };
    // Icon/title are intentionally left out of these deps and kept in sync by the effect below
    // instead — recreating the control on every showVegetation/t/theme change would re-add it,
    // and since MapLibre prepends new bottom-right controls, it'd jump back to the top of the
    // stack each time.
  }, [usesPMTiles, map, initialized, tierViewMode]);
  useEffect(() => {
    vegetationControlRef.current?.update({
      icon: showVegetation ? /*#__PURE__*/React.createElement(_VisibilityOutlined, {
        sx: {
          color: '#326AC2'
        }
      }) : /*#__PURE__*/React.createElement(_VisibilityOffOutlined, null),
      title: t(showVegetation ? 'commons.challenges.exploration_map.map.hide_vegetation' : 'commons.challenges.exploration_map.map.show_vegetation')
    });
  }, [showVegetation, t, theme]);

  // Hover a tile: subtle glow + pointer cursor, so the grid reads as interactive.
  useEffect(() => {
    if (!usesPMTiles || !map || !initialized || !sourceLayer) return;
    let hoveredId = null;
    function setHover(id, hover) {
      if (id === undefined) return;
      map.setFeatureState({
        source: gamePMTilesSourceId,
        sourceLayer: sourceLayer,
        id
      }, {
        hover
      });
    }
    function handleMouseMove(event) {
      const id = event.features?.[0]?.id;
      if (id === hoveredId) return;

      // Don't clear the popup-selected tile's glow just because the cursor moved off it.
      if (hoveredId !== null && hoveredId !== selectedTileIdRef.current) {
        setHover(hoveredId, false);
      }
      hoveredId = id ?? null;
      setHover(id, true);
      map.getCanvas().style.cursor = 'pointer';
    }
    function handleMouseLeave() {
      if (hoveredId !== null && hoveredId !== selectedTileIdRef.current) {
        setHover(hoveredId, false);
      }
      hoveredId = null;
      map.getCanvas().style.cursor = '';
    }
    map.on('mousemove', bfgPmtilesLayerId, handleMouseMove);
    map.on('mouseleave', bfgPmtilesLayerId, handleMouseLeave);
    return () => {
      map.off('mousemove', bfgPmtilesLayerId, handleMouseMove);
      map.off('mouseleave', bfgPmtilesLayerId, handleMouseLeave);
    };
  }, [usesPMTiles, map, initialized, sourceLayer]);

  // Click a tile: show its points in a popup, like the reference design.
  useEffect(() => {
    if (!usesPMTiles || !map || !initialized || !sourceLayer) return;
    let selectedId = null;
    let popup = null;
    function setSelectedHover(id, hover) {
      map.setFeatureState({
        source: gamePMTilesSourceId,
        sourceLayer: sourceLayer,
        id
      }, {
        hover
      });
    }
    function clearSelection() {
      if (selectedId !== null) setSelectedHover(selectedId, false);
      selectedId = null;
      selectedTileIdRef.current = null;
      popup?.remove();
      popup = null;
    }
    function handleClick(event) {
      const feature = event.features?.[0];
      const id = feature?.id;
      if (!feature || id === undefined) return;
      clearSelection();
      selectedId = id;
      selectedTileIdRef.current = id;
      setSelectedHover(id, true);
      const points = getBfgTeamValue(feature.properties, colorMode, myTeamId);
      const tier = getBfgIconTier(feature.properties, colorMode, myTeamId);
      const quantile = getBfgTeamQuantile(feature.properties, colorMode, myTeamId);
      const heatmapColor = quantile ? getBfgHeatmapFillColor(heatmapQuantiles, quantile) : undefined;
      const h3Index = feature.properties?.h3_index;
      const center = typeof h3Index === 'string' ? (() => {
        const [lat, lng] = cellToLatLng(h3Index);
        return {
          lat,
          lng
        };
      })() : event.lngLat;
      popup = new Popup({
        className: 'bfg-tile-popup',
        closeButton: true,
        closeOnClick: false,
        offset: 6
      }).setLngLat(center).setHTML(buildBfgPopupHtml(tier, points, colorMode, t, isDarkTheme, tierViewMode, heatmapColor)).addTo(map);
      const closedId = id;
      popup.on('close', () => {
        if (selectedId === closedId) clearSelection();
      });
    }
    map.on('click', bfgPmtilesLayerId, handleClick);
    return () => {
      map.off('click', bfgPmtilesLayerId, handleClick);
      clearSelection();
    };
  }, [usesPMTiles, map, initialized, sourceLayer, colorMode, myTeamId, t, isDarkTheme, tierViewMode, heatmapQuantilesKey]);
  const sitesWithPlace = (sites ?? []).filter(site => site.place);
  const siteOptions = sitesWithPlace.length > 0 ? sitesWithPlace : headquartersGeogroup?.place ? [headquartersGeogroup] : [];
  const filteredSiteOptions = siteSearchQuery ? siteOptions.filter(site => site.title?.toLowerCase().includes(siteSearchQuery.toLowerCase())) : siteOptions;
  function flyToSite(site) {
    if (!map || !site.place) return;
    const [lng, lat] = site.place.point.coordinates;
    map.flyTo({
      center: {
        lat,
        lng
      },
      zoom: bfgSiteZoom
    });
    setSiteMenuOpen(false);
    setSiteSearchQuery('');
  }

  // The DOM-overlaid UI (score card, legend, toolbar) already isn't part of this — it's plain HTML
  // absolutely positioned over the canvas, not drawn onto it. What toDataURL needs help with is
  // the canvas itself: MapLibre defaults to preserveDrawingBuffer: false, so its WebGL buffer is
  // cleared right after each frame is presented, and toDataURL() called straight from a click
  // handler reads that already-cleared (blank) buffer. Forcing one more render and reading the
  // canvas from inside that render's own callback catches it before it's cleared again.
  function downloadMapImage() {
    if (!map) return;
    trackEvent('Exploration Map Image Downloaded', {
      game_id: gameId
    });
    map.once('render', () => {
      const link = document.createElement('a');
      link.download = `${game?.title || 'carte-exploration'}.png`;
      link.href = map.getCanvas().toDataURL('image/png');
      link.click();
    });
    map.triggerRepaint();
  }
  if (!game) return null;
  return /*#__PURE__*/React.createElement(_Box, {
    ref: bleedRootRef,
    sx: {
      // Break out of the companies page layout's own padding AND its 1080px-capped, centered
      // content column (PageLayout, packages/companies/src/layouts/page/index.tsx) — only this
      // card, not the rest of the page. The padding-only fallback (same breakpoints, negated)
      // covers the first paint before the effect above measures and refines it; see
      // #page-layout-content in that file for the measuring target.
      marginLeft: bleedMargins ? `${bleedMargins.marginLeft}px` : {
        xs: -2,
        sm: -3,
        md: '-64px'
      },
      width: bleedMargins ? `${bleedMargins.width}px` : undefined
    }
  }, usesPMTiles && /*#__PURE__*/React.createElement(_GlobalStyles, {
    styles: {
      '@keyframes bfgPopupPop': {
        '0%': {
          transform: 'scale(0.85)',
          opacity: 0
        },
        '100%': {
          transform: 'scale(1)',
          opacity: 1
        }
      },
      '.bfg-tile-popup .maplibregl-popup-content': {
        background: uiColors.surface,
        borderRadius: 14,
        // Right padding reserves room for the absolutely-positioned close button (see below)
        // regardless of the content's shrink-to-fit width, so a longer growth-stage name
        // (eg. "Arbrisseau") never runs under it.
        padding: '12px 34px 12px 18px',
        overflow: 'hidden',
        boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
        animation: 'bfgPopupPop 180ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        width: 'max-content'
      },
      '.bfg-tile-popup .maplibregl-popup-close-button': {
        color: isDarkTheme ? uiColors.textSecondary : undefined,
        padding: 8,
        zIndex: 1
      },
      // Site marker hover tooltip (figma.com/design/K4kH3Lz6mlMbw1icekSHP2?node-id=6220-292549).
      '.bfg-site-popup .maplibregl-popup-content': {
        background: uiColors.surface,
        borderRadius: '9px',
        padding: '8px 10px 8px 8px',
        boxShadow: '0px 4px 2.5px rgba(69,69,69,0.4), 0px 2.3px 2.9px rgba(114,114,114,0.25)'
      },
      // MapLibre colors the little anchor triangle to match the popup content's own
      // background — via a *single* per-anchor-direction border-color (the tip is a 0-width
      // box with the other three border sides already `none`, that's what makes it a
      // triangle rather than a square) — so this has to mirror MapLibre's own per-direction
      // selectors (maplibre-gl.css) one-for-one rather than setting all four border colors
      // at once, which would fill in the other three sides too and square it off.
      ...(isDarkTheme ? {
        [['.bfg-tile-popup.maplibregl-popup-anchor-top .maplibregl-popup-tip', '.bfg-tile-popup.maplibregl-popup-anchor-top-left .maplibregl-popup-tip', '.bfg-tile-popup.maplibregl-popup-anchor-top-right .maplibregl-popup-tip', '.bfg-site-popup.maplibregl-popup-anchor-top .maplibregl-popup-tip', '.bfg-site-popup.maplibregl-popup-anchor-top-left .maplibregl-popup-tip', '.bfg-site-popup.maplibregl-popup-anchor-top-right .maplibregl-popup-tip'].join(', ')]: {
          borderBottomColor: uiColors.surface
        },
        [['.bfg-tile-popup.maplibregl-popup-anchor-bottom .maplibregl-popup-tip', '.bfg-tile-popup.maplibregl-popup-anchor-bottom-left .maplibregl-popup-tip', '.bfg-tile-popup.maplibregl-popup-anchor-bottom-right .maplibregl-popup-tip', '.bfg-site-popup.maplibregl-popup-anchor-bottom .maplibregl-popup-tip', '.bfg-site-popup.maplibregl-popup-anchor-bottom-left .maplibregl-popup-tip', '.bfg-site-popup.maplibregl-popup-anchor-bottom-right .maplibregl-popup-tip'].join(', ')]: {
          borderTopColor: uiColors.surface
        },
        [['.bfg-tile-popup.maplibregl-popup-anchor-left .maplibregl-popup-tip', '.bfg-site-popup.maplibregl-popup-anchor-left .maplibregl-popup-tip'].join(', ')]: {
          borderRightColor: uiColors.surface
        },
        [['.bfg-tile-popup.maplibregl-popup-anchor-right .maplibregl-popup-tip', '.bfg-site-popup.maplibregl-popup-anchor-right .maplibregl-popup-tip'].join(', ')]: {
          borderLeftColor: uiColors.surface
        }
      } : {}),
      // Zoom +/- controls (figma.com/design/K4kH3Lz6mlMbw1icekSHP2?node-id=4613-155998):
      // merged into one pill instead of two separately-floating, gapped round FABs. Scoped
      // under this map's own container id so no other map on the page is affected — NOT via
      // ButtonControl's per-control id (see addPrefix in hooks/map/index.tsx): that id lands
      // on a plain div that gets wiped out the moment createRoot(this.container).render(...)
      // takes over, since React owns the whole .maplibregl-ctrl node from then on. Controls
      // stack in this map's bottom-right corner in add order, but MapLibre *prepends* each
      // newly-added one above the others already there (see the vegetation-control effect
      // above) — so add order top-to-bottom ends up: vegetation (added last, 1st), zoom-out
      // (2nd), zoom-in (3rd), locate (4th, added below). nth-of-type picks each one
      // explicitly rather than first/last-of-type, which would only be correct as long as
      // there were exactly this many controls in that corner.
      [`#${containerId} .maplibregl-ctrl-bottom-right .maplibregl-ctrl:nth-of-type(1) .MuiFab-root`]: {
        backgroundColor: uiColors.surface,
        borderRadius: '4px',
        boxShadow: '0px 2px 1.5px rgba(109,109,109,0.25)',
        color: uiColors.textPrimary
      },
      [`#${containerId} .maplibregl-ctrl-bottom-right .maplibregl-ctrl:nth-of-type(2)`]: {
        marginBottom: '0 !important'
      },
      [`#${containerId} .maplibregl-ctrl-bottom-right .maplibregl-ctrl:nth-of-type(2) .MuiFab-root`]: {
        backgroundColor: uiColors.surface,
        borderRadius: '4px 4px 0 0',
        boxShadow: '0px 2px 1.5px rgba(109,109,109,0.25)',
        color: uiColors.textPrimary
      },
      [`#${containerId} .maplibregl-ctrl-bottom-right .maplibregl-ctrl:nth-of-type(3) .MuiFab-root`]: {
        backgroundColor: uiColors.surface,
        borderRadius: '0 0 4px 4px',
        borderTop: `1px solid ${isDarkTheme ? uiColors.border : '#C7CEDC'}`,
        boxShadow: '0px 2px 1.5px rgba(109,109,109,0.25)',
        color: uiColors.textPrimary
      },
      // Locate-me button (4th control) — same radius/shadow treatment, kept as its own
      // separate button below the merged pill rather than merged into it.
      [`#${containerId} .maplibregl-ctrl-bottom-right .maplibregl-ctrl:nth-of-type(4) .MuiFab-root`]: {
        backgroundColor: uiColors.surface,
        borderRadius: '4px',
        boxShadow: '0px 2px 1.5px rgba(109,109,109,0.25)',
        color: uiColors.textPrimary
      }
    }
  }), /*#__PURE__*/React.createElement(_Box, {
    overflow: "hidden",
    sx: {
      boxShadow: '0 20px 50px -16px rgba(27,94,32,0.45), 0 4px 16px rgba(0,0,0,0.1)'
    }
  }, /*#__PURE__*/React.createElement(_Box, {
    position: "relative",
    sx: {
      background: uiColors.background,
      height: bleedMargins ? `${bleedMargins.height}px` : 500,
      minHeight: {
        xs: 320,
        sm: 500
      }
    },
    width: "100%"
  }, /*#__PURE__*/React.createElement(_Box, {
    height: "100%",
    id: containerId,
    sx: {
      // Hides the brief flash of the default style before the game's own style has
      // loaded and swapped in (see the setStyle effect above).
      opacity: initialized ? 1 : 0,
      transition: 'opacity 0.15s ease-in',
      WebkitTouchCallout: 'none',
      WebkitUserSelect: 'none',
      userSelect: 'none'
    },
    width: "100%"
  }), /*#__PURE__*/React.createElement(_Box, {
    bottom: 0,
    left: 0,
    position: "absolute",
    right: 0,
    sx: {
      background: 'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.08) 100%)',
      pointerEvents: 'none'
    },
    top: 0
  }), usesPMTiles && zoom !== null && zoom < bfgMinZoom && /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    bottom: 0,
    display: "flex",
    justifyContent: "center",
    left: 0,
    position: "absolute",
    right: 0,
    sx: {
      background: 'rgba(44,46,48,0.35)',
      pointerEvents: 'none'
    },
    top: 0
  }, /*#__PURE__*/React.createElement(_Box, {
    bgcolor: uiColors.surface,
    borderRadius: 999,
    boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
    color: uiColors.textPrimary,
    fontSize: 13,
    fontWeight: 600,
    onClick: () => map?.easeTo({
      zoom: 10,
      duration: 300
    }),
    paddingX: 2,
    paddingY: 1,
    sx: {
      cursor: 'pointer',
      pointerEvents: 'auto'
    }
  }, t('commons.challenges.exploration_map.map.zoom_in_cta'))), usesPMTiles && /*#__PURE__*/React.createElement(_Box, {
    bgcolor: uiColors.surfaceMuted,
    borderRadius: 999,
    display: "flex",
    left: 12,
    maxWidth: "calc(100vw - 24px)",
    overflow: "auto",
    padding: 0.5,
    position: "absolute",
    sx: {
      backdropFilter: 'blur(6px)'
    },
    top: 12
  }, colorModes.map(({
    key,
    label,
    shortLabel
  }) => /*#__PURE__*/React.createElement(_Tooltip, {
    enterTouchDelay: 500,
    key: key,
    title: label
  }, /*#__PURE__*/React.createElement(_Box, {
    onClick: () => {
      trackEvent('Exploration Map Color Mode Changed', {
        color_mode: key,
        game_id: gameId
      });
      setColorMode(key);
    },
    sx: {
      bgcolor: colorMode === key ? uiColors.surface : 'transparent',
      borderRadius: 999,
      boxShadow: colorMode === key ? '0 1px 4px rgba(0,0,0,0.15)' : 'none',
      color: colorMode === key ? uiColors.textPrimary : uiColors.textSecondary,
      cursor: 'pointer',
      flexShrink: 0,
      fontSize: {
        xs: 12,
        sm: 13
      },
      fontWeight: colorMode === key ? 700 : 600,
      paddingX: {
        xs: 1.25,
        sm: 2
      },
      paddingY: 0.875,
      transition: 'background-color 150ms ease, color 150ms ease',
      userSelect: 'none',
      whiteSpace: 'nowrap'
    }
  }, /*#__PURE__*/React.createElement(_Box, {
    component: "span",
    sx: {
      display: {
        xs: 'none',
        sm: 'inline'
      }
    }
  }, label), /*#__PURE__*/React.createElement(_Box, {
    component: "span",
    sx: {
      display: {
        xs: 'inline',
        sm: 'none'
      }
    }
  }, shortLabel))))), usesPMTiles && /*#__PURE__*/React.createElement(_Box, {
    alignItems: "flex-start",
    display: "flex",
    gap: 1,
    maxWidth: "calc(100vw - 24px)",
    position: "absolute",
    right: 12,
    top: 12
  }, !isWebview && /*#__PURE__*/React.createElement(_Box, {
    bgcolor: isDarkTheme ? uiColors.surfaceMuted : '#EEEFF3',
    borderRadius: "999px",
    display: "flex",
    flexShrink: 0,
    padding: "4px"
  }, tierViewModes.map(({
    key,
    label,
    tooltipBody,
    tooltipTitle
  }) => /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    gap: "4px",
    key: key,
    onClick: () => setTierViewMode(key),
    sx: {
      bgcolor: tierViewMode === key ? uiColors.surface : 'transparent',
      borderRadius: tierViewMode === key ? '999px' : '6px',
      boxShadow: tierViewMode === key ? '0px 3px 4px rgba(0,0,0,0.12), 0px 3px 0.5px rgba(0,0,0,0.04)' : 'none',
      cursor: 'pointer',
      paddingX: tierViewMode === key ? '8px' : '5px',
      paddingY: '5px',
      userSelect: 'none',
      whiteSpace: 'nowrap'
    }
  }, /*#__PURE__*/React.createElement(_Typography, {
    color: tierViewMode === key ? uiColors.textPrimary : uiColors.textSecondary,
    fontSize: 14,
    fontWeight: 600
  }, label), /*#__PURE__*/React.createElement(_Tooltip, {
    arrow: true,
    enterTouchDelay: 500,
    placement: "top",
    slotProps: {
      arrow: {
        sx: {
          color: '#2C2E30'
        }
      },
      tooltip: {
        sx: {
          bgcolor: '#2C2E30',
          borderRadius: '8px',
          boxShadow: '0px 2px 5px rgba(114,114,114,0.25)',
          maxWidth: 236,
          padding: '8px'
        }
      }
    },
    title: /*#__PURE__*/React.createElement(_Box, {
      display: "flex",
      flexDirection: "column",
      gap: "4px"
    }, /*#__PURE__*/React.createElement(_Typography, {
      color: "#fff",
      fontSize: 12,
      fontWeight: 700
    }, tooltipTitle), /*#__PURE__*/React.createElement(_Typography, {
      color: "#fff",
      fontSize: 12,
      fontWeight: 400
    }, tooltipBody))
  }, /*#__PURE__*/React.createElement(_InfoOutlined, {
    onClick: event => event.stopPropagation(),
    sx: {
      color: uiColors.textMuted,
      fontSize: 16
    }
  }))))), /*#__PURE__*/React.createElement(_Box, {
    position: "relative"
  }, /*#__PURE__*/React.createElement(_Tooltip, {
    enterTouchDelay: 500,
    title: t('commons.challenges.exploration_map.map.site_button')
  }, /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    bgcolor: uiColors.surface,
    borderRadius: "8px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
    display: "flex",
    gap: 0.75,
    onClick: () => setSiteMenuOpen(open => !open),
    paddingX: {
      xs: 1,
      sm: 1.75
    },
    paddingY: 0.875,
    sx: {
      cursor: 'pointer',
      userSelect: 'none',
      whiteSpace: 'nowrap'
    }
  }, /*#__PURE__*/React.createElement(_Typography, {
    color: uiColors.textPrimary,
    fontSize: 13,
    fontWeight: 600,
    sx: {
      display: {
        xs: 'none',
        sm: 'block'
      }
    }
  }, t('commons.challenges.exploration_map.map.site_button')), /*#__PURE__*/React.createElement(_ExpandMore, {
    sx: {
      color: uiColors.textSecondary,
      fontSize: 18,
      transform: siteMenuOpen ? 'rotate(180deg)' : 'none',
      transition: 'transform 150ms ease'
    }
  }))), siteMenuOpen && /*#__PURE__*/React.createElement(_Box, {
    bgcolor: uiColors.surface,
    borderRadius: 2,
    boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
    overflow: "hidden",
    position: "absolute",
    right: 0,
    sx: {
      marginTop: '8px',
      maxWidth: 'calc(100vw - 24px)'
    },
    top: "100%",
    width: 260,
    zIndex: 2
  }, /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    gap: 1,
    paddingX: 1.5,
    paddingY: 1,
    sx: {
      borderBottom: `1px solid ${uiColors.border}`
    }
  }, /*#__PURE__*/React.createElement(_Search, {
    sx: {
      color: uiColors.textMuted,
      fontSize: 18
    }
  }), /*#__PURE__*/React.createElement(_InputBase, {
    autoFocus: true,
    onChange: event => setSiteSearchQuery(event.target.value),
    placeholder: t('commons.challenges.exploration_map.map.search_site_placeholder'),
    sx: {
      color: uiColors.textPrimary,
      fontSize: 13,
      width: '100%',
      '& input::placeholder': {
        color: uiColors.textMuted,
        opacity: 1
      }
    },
    value: siteSearchQuery
  })), /*#__PURE__*/React.createElement(_Box, {
    maxHeight: 220,
    overflow: "auto"
  }, filteredSiteOptions.length === 0 ? /*#__PURE__*/React.createElement(_Typography, {
    color: uiColors.textMuted,
    fontSize: 12,
    padding: 1.5
  }, t('commons.challenges.exploration_map.map.no_site_found')) : filteredSiteOptions.map(site => /*#__PURE__*/React.createElement(_Box, {
    key: site.id,
    onClick: () => flyToSite(site),
    paddingX: 1.5,
    paddingY: 1,
    sx: {
      cursor: 'pointer',
      '&:hover': {
        bgcolor: isDarkTheme ? '#333' : '#F7F8FA'
      }
    }
  }, /*#__PURE__*/React.createElement(_Typography, {
    color: uiColors.textPrimary,
    fontSize: 13,
    fontWeight: 700
  }, site.title), site.place && /*#__PURE__*/React.createElement(_Typography, {
    color: uiColors.textMuted,
    fontSize: 11,
    marginTop: 0.25
  }, site.place.address)))))), !isWebview && /*#__PURE__*/React.createElement(_Tooltip, {
    enterTouchDelay: 500,
    title: t('commons.challenges.exploration_map.map.download')
  }, /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    bgcolor: "#326AC2",
    borderRadius: "8px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
    display: "flex",
    gap: 0.75,
    onClick: downloadMapImage,
    paddingX: {
      xs: 1,
      sm: 1.75
    },
    paddingY: 0.875,
    sx: {
      cursor: 'pointer',
      userSelect: 'none',
      whiteSpace: 'nowrap'
    }
  }, /*#__PURE__*/React.createElement(_Download, {
    sx: {
      color: '#fff',
      fontSize: 18
    }
  }), /*#__PURE__*/React.createElement(_Typography, {
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    sx: {
      display: {
        xs: 'none',
        sm: 'block'
      }
    }
  }, t('commons.challenges.exploration_map.map.download'))))), usesPMTiles && /*#__PURE__*/React.createElement(_Box, {
    alignItems: "flex-start",
    bottom: 12,
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
    left: 12,
    maxWidth: "calc(100vw - 24px)",
    position: "absolute"
  }, myTeam && /*#__PURE__*/React.createElement(_Box, {
    bgcolor: "#005A44",
    borderRadius: 2,
    boxShadow: "0 4px 16px rgba(0,0,0,0.16)",
    paddingX: {
      xs: 1.5,
      sm: 2
    },
    paddingY: 1.25
  }, /*#__PURE__*/React.createElement(_Typography, {
    color: "#fff",
    fontSize: {
      xs: 13,
      sm: 14
    },
    fontWeight: 800
  }, (() => {
    const count = colorMode === 'commuting' ? myTeam.commutingVisitedTileCount : myTeam.visitedTileCount;
    return t('commons.challenges.exploration_map.map.explored_zones', {
      count,
      formatted: formatNumber(count)
    });
  })())), /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    bgcolor: uiColors.surface,
    borderRadius: 2,
    boxShadow: "0 4px 16px rgba(0,0,0,0.16)",
    display: "flex",
    flexWrap: "wrap",
    gap: {
      xs: 1,
      sm: 2
    },
    paddingX: {
      xs: 1.5,
      sm: 2
    },
    paddingY: 1.25,
    sx: {
      maxWidth: 'calc(100vw - 48px)',
      width: 'fit-content'
    }
  }, /*#__PURE__*/React.createElement(_Typography, {
    color: uiColors.textPrimary,
    fontSize: 13,
    fontWeight: 700,
    sx: {
      display: {
        xs: 'none',
        sm: 'block'
      }
    }
  }, t('commons.challenges.exploration_map.map.frequency_legend_title')), tierViewMode === 'heatmap' ?
  // Tied quantile boundaries (e.g. [1, 1, 2, 3, 112]) are merged into one row —
  // see buildBfgHeatmapLegendItems — so this is already duplicate-free; still
  // keyed by index (not rangeLabel) since that's what stays stable across
  // re-renders regardless.
  buildBfgHeatmapLegendItems(heatmapQuantiles).map(({
    color,
    rangeLabel
  }, index) => /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    gap: 0.75,
    key: index
  }, /*#__PURE__*/React.createElement(_Box, {
    borderRadius: "4px",
    height: 14,
    sx: {
      bgcolor: color,
      opacity: 0.75
    },
    width: 14
  }), /*#__PURE__*/React.createElement(_Typography, {
    color: uiColors.textSecondary,
    fontSize: 12,
    fontWeight: 600
  }, rangeLabel))) : bfgPassageBuckets.map(({
    labelKey,
    color,
    rangeLabel
  }) => /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    gap: 0.75,
    key: labelKey
  }, /*#__PURE__*/React.createElement(_Box, {
    border: "1.5px solid #1B6515",
    borderRadius: "4px",
    height: 14,
    sx: {
      // "Non exploré" (soil) matches its own on-map fill: 0 opacity, no
      // background — see bfgFillOpacityExpression — rather than the solid
      // beige swatch every other tier gets.
      bgcolor: labelKey === 'tier_soil' ? 'transparent' : color,
      opacity: labelKey === 'tier_soil' ? 1 : 0.5
    },
    width: 14
  }), /*#__PURE__*/React.createElement(_Typography, {
    color: uiColors.textSecondary,
    fontSize: 12,
    fontWeight: 600
  }, /*#__PURE__*/React.createElement(_Box, {
    component: "span",
    sx: {
      display: {
        xs: 'none',
        sm: 'inline'
      }
    }
  }, rangeLabel ?? t('commons.challenges.exploration_map.map.tier_soil')), /*#__PURE__*/React.createElement(_Box, {
    component: "span",
    sx: {
      display: {
        xs: 'inline',
        sm: 'none'
      }
    }
  }, rangeLabel ?? '0')))))))));
}
export default ExplorationMapGame;